import { isAfter } from 'date-fns';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import RangeSlider from 'react-range-slider-input';
import Select, { MultiValue } from 'react-select';
import makeAnimated from 'react-select/animated';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import busSlash from '../../../../assets/images/icons/busSlash.svg';
import busStar from '../../../../assets/images/icons/busStar.svg';
import coinsSidebar from '../../../../assets/images/icons/coinsSidebar.svg';
import info from '../../../../assets/images/icons/info.svg';
import route from '../../../../assets/images/icons/route.svg';
import user from '../../../../assets/images/icons/user.svg';
import { useAppContext } from '../../../../contexts/auth';
import useApiCall from '../../../../hooks/useApiCall';
import useErrors from '../../../../hooks/useErrors';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import { LinesAndOperatorsOptionsApiResponseType } from '../../../../pages/LinesOperatorsBias/types';
import LineFaresService from '../../../../services/LineFaresService';
import WorkplacesService from '../../../../services/WorkplacesService';
import floatToCurrency from '../../../../utils/floatToCurrency';
import formatCurrency from '../../../../utils/formatCurrency';
import isValueValid from '../../../../utils/isValueValid';
import onlyNumbers from '../../../../utils/onlyNumbers';
import parseCurrencyStringToFloat from '../../../../utils/parseCurrencyStringToFloat';
import removeDuplicates from '../../../../utils/removeDuplicates';
import { CustomStyle } from '../../../CustomSelectStyle';
import FilterRadioButton from '../../../FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../../FilterRadioButtonsContainer';
import FormGroup from '../../../FormGroup';
import Input from '../../../Input';
import MyModal from '../../../Modal';
import { DadosConsulta, LinhasBia, LinhasExcecao, OpBia, OpExcecao, OptionType } from '../../types';
import { InnerDataContainer, RangeSliderContainer, SelectedViewContainer } from './styles';

interface IEditSettingsModal {
  editSettingsDataModalShow: boolean,
  setEditSettingsDataModalShow: Dispatch<SetStateAction<boolean>>,
  consultData: DadosConsulta,
  setConsultData: Dispatch<SetStateAction<DadosConsulta>>,
  newConsultData: DadosConsulta,
  setNewConsultData: Dispatch<SetStateAction<DadosConsulta>>,
  canChooseUsesAffreightment: boolean,
  routingType: number,
  linesBias: LinhasBia[],
  setLinesBias: Dispatch<SetStateAction<LinhasBia[]>>,
  opsBias: OpBia[],
  setOpsBias: Dispatch<SetStateAction<OpBia[]>>,
  linesException: LinhasExcecao[],
  setLinesException: Dispatch<SetStateAction<LinhasExcecao[]>>,
  opsException: OpExcecao[],
  setOpsException: Dispatch<SetStateAction<OpExcecao[]>>,
  isMultiRoute: boolean,
  setIsSomethingLoading: Dispatch<SetStateAction<boolean>>,
  biasType: 'P' | 'F',
  setBiasType: Dispatch<SetStateAction<'P' | 'F'>>,
  canChangeBiasType: boolean,
}

export default function EditSettingsModal({
  consultData,
  setConsultData,
  newConsultData,
  setNewConsultData,
  editSettingsDataModalShow,
  setEditSettingsDataModalShow,
  canChooseUsesAffreightment,
  routingType,
  linesBias,
  setLinesBias,
  opsBias,
  setOpsBias,
  linesException,
  setLinesException,
  opsException,
  setOpsException,
  isMultiRoute,
  setIsSomethingLoading,
  biasType,
  setBiasType,
  canChangeBiasType,
}: IEditSettingsModal) {
  const [view, selectedView] = useState<'settings' | 'bias'>('settings');
  const [selectedWalkDistance, setSelectedWalkDistance] = useState([500, consultData.distancia]);
  const [, setWalkDistance] = useState([500, consultData.distancia]);
  const [fullLinesOptions, setFullLinesOptions] = useState<MultiValue<OptionType>>([]);
  const [fullOpsOptions, setFullOpsOptions] = useState<MultiValue<OptionType>>([]);
  const [currentLinesBias, setCurrentLinesBias] = useState(linesBias);
  const [currentOpsBias, setCurrentOpsBias] = useState(opsBias);
  const [currentLinesException, setCurrentLinesException] = useState(linesException);
  const [currentOpsException, setCurrentOpsException] = useState(opsException);

  const operatorBiasMultiValue = useMemo(() => {
    return currentOpsBias.map((op) => {
      const correspondingOperators = fullOpsOptions.filter((opt) => opt.value === String(op.codOperadora) || opt.value === String(`PV_${op.codOperadora}`));
      return correspondingOperators.map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    }).flat();
  }, [currentOpsBias, fullOpsOptions]);

  const operatorExceptionMultiValue = useMemo(() => {
    return currentOpsException.map((op) => {
      const correspondingOperators = fullOpsOptions.filter((opt) => opt.value === String(op.codOperadora) || opt.value === String(`PV_${op.codOperadora}`));
      return correspondingOperators.map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    }).flat();
  }, [currentOpsException, fullOpsOptions]);

  const lineBiasMultiValue = useMemo(() => {
    return currentLinesBias.map((line) => {
      const correspondingLine = fullLinesOptions.find((opt) => opt.value === String(line.routeID));
      return {
        value: correspondingLine?.value || '',
        label: correspondingLine?.label || '',
      };
    });
  }, [currentLinesBias, fullLinesOptions]);

  const lineExceptionMultiValue = useMemo(() => {
    return currentLinesException.map((line) => {
      const correspondingLine = fullLinesOptions.find((opt) => opt.value === String(line.routeID));
      return {
        value: correspondingLine?.value || '',
        label: correspondingLine?.label || '',
      };
    });
  }, [currentLinesException, fullLinesOptions]);

  const {
    setError, removeError, getErrorMessageByFieldName
  } = useErrors();
  const { isCustomer, usersCompanyCode, userCode, userProfile } = useGetGeneralAppData();
  const { token } = useAppContext();
  const { apiCall } = useApiCall();
  // const { selectedTheme } = useThemeContext();
  const animatedComponents = makeAnimated();

  const handleWalkDistanceSelection = useCallback((km: number[]) => {
    setWalkDistance(km);
    setNewConsultData((prevState) => ({
      ...prevState,
      distancia: km[1],
    }));
  }, [setNewConsultData]);

  function handleUsesMirroringChange(uses: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      usaEspelhamento: uses,
    }));
  }

  function handleUsesAffreightmentChange(uses: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      usaFretado: uses,
    }));
  }

  function handleCeilingValueChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      valorTeto: formatCurrency(event.target.value)
    }));

    if (!event.target.value) {
      setError({ field: 'ceilingValue', message: 'Defina um valor máximo de VT'});
    } else if ((!isValueValid(parseCurrencyStringToFloat(formatCurrency(event.target.value)), 15, 100))) {
      setError({ field: 'ceilingValue', message: `Valor deve ser entre ${formatCurrency('15,00')} e ${formatCurrency('100,00')}`});
    } else {
      removeError('ceilingValue');
    }
  }

  function handleInitialValueChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      valorVT: formatCurrency(event.target.value)
    }));

    if (!event.target.value) {
      setError({ field: 'initialValue', message: 'Defina um valor inicial de VT'});
    } else if ((!isValueValid(parseCurrencyStringToFloat(formatCurrency(event.target.value)), 3, 100))) {
      setError({ field: 'initialValue', message: `Valor deve ser entre ${formatCurrency('3,00')} e ${formatCurrency('100,00')}`});
    } else {
      removeError('initialValue');
    }
  }

  function handleUsesNoTaxTickets(uses: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      semTaxaVT: uses,
    }));
  }

  function handleUsesNoTaxTicketsAesa(uses: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      regraVTSemTxAesa: uses,
    }));
  }

  function handleRJIntegration(integrates: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      semIntegraVT: !integrates,
    }));
  }

  function handleEntryTimeChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setNewConsultData((prevState) => ({
      ...prevState,
      horaEntra: value
    }));

    const [hours, minutes] = value.split(':');
    const entryTime = new Date();
    entryTime.setHours(Number(hours));
    entryTime.setMinutes(Number(minutes));

    const [exitHour, exitMinute] = newConsultData.horaSai.split(':');
    const exitTime = new Date();
    exitTime.setHours(Number(exitHour));
    exitTime.setMinutes(Number(exitMinute));

    if (!value) {
      setError({ field: 'entryTime', message: 'Horário de entrada é obrigatório'});
    } else if (isAfter(entryTime, exitTime)) {
      setError({ field: 'entryTime', message: 'Horário de entrada deve ser antes do horário de saída'});
      setError({ field: 'exitTime', message: 'Horário de saída deve ser após horário de entrada'});
    } else {
      removeError('entryTime');
      removeError('exitTime');
    }
  }

  function handleExitTimeChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setNewConsultData((prevState) => ({
      ...prevState,
      horaSai: value
    }));

    const [hours, minutes] = value.split(':');
    const exitTime = new Date();
    exitTime.setHours(Number(hours));
    exitTime.setMinutes(Number(minutes));

    const [entryHour, entryMinute] = newConsultData.horaEntra.split(':');
    const entryTime = new Date();
    entryTime.setHours(Number(entryHour));
    entryTime.setMinutes(Number(entryMinute));

    if (!value) {
      setError({ field: 'exitTime', message: 'Horário de saída é obrigatório'});
    } else if (isAfter(entryTime, exitTime)) {
      setError({ field: 'entryTime', message: 'Horário de entrada deve ser antes do horário de saída'});
      setError({ field: 'exitTime', message: 'Horário de saída deve ser após horário de entrada'});
    } else {
      removeError('entryTime');
      removeError('exitTime');
    }
  }

  function handleWorkDaysChange(event: ChangeEvent<HTMLInputElement>) {
    const onlyNumbersValue = onlyNumbers(event.target.value);

    setNewConsultData((prevState) => ({
      ...prevState,
      diasVT: onlyNumbersValue,
    }));

    if (!isValueValid(onlyNumbersValue, 1, 30)) {
      setError({ field: 'workDays', message: 'Turno deve estar entre 1 e 30 dias!' });
    } else {
      removeError('name');
    }
  }

  function handleIsHighSalaryChange(isHigh: boolean) {
    setNewConsultData((prevState) => ({
      ...prevState,
      salario: isHigh ? 3500 : 2000
    }));
  }

  function changeBiasType(type: 'P' | 'F') {
    setBiasType(type);
    setCurrentOpsBias((prevState) => prevState.map((opt) => ({ ...opt, tipoBias: type })));
    setCurrentLinesBias((prevState) => prevState.map((opt) => ({ ...opt, tipoBias: type })));
  }

  function confirmChanges() {
    setEditSettingsDataModalShow(false);
    setConsultData({
      ...newConsultData,
      valorTeto: parseCurrencyStringToFloat(String(newConsultData.valorTeto)),
      valorVT: parseCurrencyStringToFloat(String(newConsultData.valorVT)),
    });
    setOpsBias(currentOpsBias);
    setLinesBias(currentLinesBias);
    setOpsException(currentOpsException);
    setLinesException(currentLinesException);
  }

  const loadOptions = (inputValue: string, callback: (options: any[]) => void) => {
    if (inputValue.length < 3) {
      callback([]); // só carrega se tiver pelo menos 3 caracteres
      return;
    }

    // Supondo que `fullLinesOptions` já está carregado localmente:
    const filtered = fullLinesOptions.filter((opt) =>
      opt.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    callback(filtered);
  };

  const loadOperatorsAndLinesOptions = useCallback(async () => {
    await apiCall({
      apiToCall: WorkplacesService.getOperatorsAndLinesOptions,
      queryParams: {
        codEmpresa: usersCompanyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
      },
      onStartLoad: () => setIsSomethingLoading(true),
      onEndLoad: () => setIsSomethingLoading(false),
      actionAfterResponse: (apiResponse: LinesAndOperatorsOptionsApiResponseType) => {
        if (!apiResponse.codigo) {
          toast.warn('Não foi possível recuperar as opções de linhas e operadoras para carregar o input de bias e exceõoes.');
          return;
        }
        const defaultOpsOptions = apiResponse.listaOperadoras?.map((x) => ({ value: String(x.codOperadora), label: x.nomeOperadora }));
        const pavelOpsOptions = Array.from(
          apiResponse.listaLinhas
            ?.filter((line) => !!line.codOperadoraPavel)
            .reduce((map, line) => {
              const value = String(`PV_${line.codOperadoraPavel}`);
              if (!map.has(value)) {
                map.set(value, { value, label: line.nomeOperadoraPavel });
              }
              return map;
            }, new Map<string, { value: string; label: string; }>()).values()
        );
        setFullOpsOptions(removeDuplicates([...defaultOpsOptions, ...pavelOpsOptions]));

        const baseLines = apiResponse.listaLinhas || [];

        const opsMap = new Map<string, string>();
        apiResponse.listaOperadoras?.forEach((op) => {
          if (op.nomeOperadora) {
            opsMap.set(String(op.codOperadora), op.nomeOperadora);
          }
        });

        const getOperatorName = (line: (typeof baseLines)[0]) => {
          const opName =
            (line as any).nomeOperadora ||
            opsMap.get(String(line.codOperadora)) ||
            line.nomeOperadoraPavel ||
            (line as any).nomeOp ||
            '';
          return typeof opName === 'string' ? opName.trim() : '';
        };

        const formatLineLabel = (line: (typeof baseLines)[0], tarifa?: number | null) => {
          const opName = getOperatorName(line);
          const opLabel = opName ? ` - ${opName}` : '';
          const fareLabel = tarifa != null ? ` - ${floatToCurrency(tarifa)}` : '';
          return `${line.letreiro} - ${line.sentido} (${line.routeID})${opLabel}${fareLabel}`;
        };

        setFullLinesOptions(
          baseLines.map((x) => ({
            value: x.routeID,
            label: formatLineLabel(x),
          }))
        );

        LineFaresService.getFaresMap({
          token,
          codUsu: userCode,
          perfilUsu: userProfile,
        })
          .then((faresMap) => {
            setFullLinesOptions(
              baseLines.map((x) => ({
                value: x.routeID,
                label: formatLineLabel(x, faresMap.get(String(x.routeID).trim())),
              }))
            );
          })
          .catch((err) => {
            console.warn('Erro ao carregar tarifas de linhas no modal:', err);
          });
      },
    });
  }, [apiCall, setIsSomethingLoading, token, userCode, userProfile, usersCompanyCode]);

  useEffect(() => {
    setNewConsultData({
      ...consultData,
      valorTeto: consultData.valorTeto ? floatToCurrency(consultData.valorTeto) : 'Não utiliza',
      valorVT: consultData.valorVT ? floatToCurrency(consultData.valorVT) : 0
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentLinesBias(linesBias);
    setCurrentOpsBias(opsBias);
    setCurrentLinesException(linesException);
    setCurrentOpsException(opsException);
    if ((!fullOpsOptions.length || !fullLinesOptions.length) && editSettingsDataModalShow) {
      loadOperatorsAndLinesOptions();
    }
  }, [editSettingsDataModalShow, fullOpsOptions, fullLinesOptions, loadOperatorsAndLinesOptions, linesBias, opsBias, linesException, opsException]);

  return (
    <MyModal
      show={editSettingsDataModalShow}
      title="Editar critérios da consulta"
      closeButtonLabel="Fechar"
      actionButtonLabel="Confirmar"
      onClose={() => setEditSettingsDataModalShow(false)}
      onAction={() => confirmChanges()}
      // isActionButtonDisabled={!isValidToConfirm}
      type="action"
      size='xl'
      modalBody={(
        <>
          {(!isCustomer && !isMultiRoute) && (
            <FilterRadioButtonsContainer marginBottom="20px">
              <FilterRadioButton selected={view === 'settings'} onClick={() => selectedView('settings')}>
            Critérios Gerais
              </FilterRadioButton>
              <FilterRadioButton selected={view === 'bias'} onClick={() => selectedView('bias')}>
            Bias / Exceções
              </FilterRadioButton>
            </FilterRadioButtonsContainer>
          )}

          {view === 'settings' && (
            <SelectedViewContainer>
              <InnerDataContainer>
                <h4>
                  <img src={route} alt="" />
                Critérios de Rota
                </h4>
                <FormGroup>
                  <label htmlFor="contactName">Espelhamento</label>
                  <FilterRadioButtonsContainer>
                    <FilterRadioButton selected={newConsultData.usaEspelhamento} onClick={() => handleUsesMirroringChange(true)}>
                    Utiliza
                    </FilterRadioButton>
                    <FilterRadioButton selected={!newConsultData.usaEspelhamento} onClick={() => handleUsesMirroringChange(false)}>
                    Não Utiliza
                    </FilterRadioButton>
                  </FilterRadioButtonsContainer>
                </FormGroup>

                {canChooseUsesAffreightment && (
                  <FormGroup>
                    <label htmlFor="contactName">Fretamento</label>
                    <FilterRadioButtonsContainer>
                      <FilterRadioButton selected={newConsultData.usaFretado} onClick={() => handleUsesAffreightmentChange(true)}>
                    Utiliza
                      </FilterRadioButton>
                      <FilterRadioButton selected={!newConsultData.usaFretado} onClick={() => handleUsesAffreightmentChange(false)}>
                    Não Utiliza
                      </FilterRadioButton>
                    </FilterRadioButtonsContainer>
                  </FormGroup>
                )}

                <FormGroup>
                  <label htmlFor="contactName">Limite de distância caminhada</label>
                  <RangeSliderContainer>
                    <header>
                      {` ${selectedWalkDistance[1]}m`}
                    </header>
                    <RangeSlider
                      min={500}
                      max={2000}
                      value={selectedWalkDistance}
                      onThumbDragEnd={() => handleWalkDistanceSelection(selectedWalkDistance)}
                      onInput={(evt: number[]) => setSelectedWalkDistance(evt)}
                      className="single-thumb"
                      thumbsDisabled={[true, false]}
                      step={50}
                      rangeSlideDisabled
                    />
                  </RangeSliderContainer>
                </FormGroup>
              </InnerDataContainer>

              <InnerDataContainer>
                <h4>
                  <img src={coinsSidebar} alt="" />
                Critérios Financeiros
                </h4>
                <FormGroup error={getErrorMessageByFieldName('ceilingValue')}>
                  <label htmlFor="contactName">Valor Teto</label>
                  <Input
                    placeholder="R$ XX,XX"
                    value={newConsultData.valorTeto || 'Não utiliza'}
                    disabled={!newConsultData.usaTeto}
                    onChange={handleCeilingValueChange}
                    autoComplete="new-password"
                    darkBorder
                    error={getErrorMessageByFieldName('ceilingValue')}
                  />
                </FormGroup>
                {routingType === 2 && (
                  <FormGroup error={getErrorMessageByFieldName('initialValue')}>
                    <label htmlFor="contactName">Valor Inicial</label>
                    <Input
                      placeholder="R$ XX,XX"
                      value={newConsultData.valorVT}
                      onChange={handleInitialValueChange}
                      autoComplete="new-password"
                      darkBorder
                      error={getErrorMessageByFieldName('initialValue')}
                      disabled={isCustomer}
                    />
                  </FormGroup>
                )}
                <FormGroup>
                  <label htmlFor="contactName">
                  SPTRANS (Bilhete comum ou VT)
                    <img src={info} alt="info" className='primaryColor info' title="O VT possui um valor ligeiramente mais alto que o vale comum, por isso algumas empresas optam por realizar a recarga como vale comum, ou reembolsar o colaborador em dinheiro." />
                  </label>
                  <FilterRadioButtonsContainer>
                    <FilterRadioButton selected={!newConsultData.semTaxaVT} onClick={() => handleUsesNoTaxTickets(false)}>
                  VT
                    </FilterRadioButton>
                    <FilterRadioButton selected={newConsultData.semTaxaVT} onClick={() => handleUsesNoTaxTickets(true)}>
                    Bilhete Comum {isCustomer ? '(mediante liminar da justiça)' : ''}
                    </FilterRadioButton>
                  </FilterRadioButtonsContainer>
                </FormGroup>

                <FormGroup>
                  <label htmlFor="contactName">
                  AESA (Bilhete comum ou VT)
                    <img src={info} alt="info" className='primaryColor info' title="O VT possui um valor ligeiramente mais alto que o vale comum, por isso algumas empresas optam por realizar a recarga como vale comum, ou reembolsar o colaborador em dinheiro." />
                  </label>
                  <FilterRadioButtonsContainer>
                    <FilterRadioButton selected={!newConsultData.regraVTSemTxAesa} onClick={() => handleUsesNoTaxTicketsAesa(false)}>
                  VT
                    </FilterRadioButton>
                    <FilterRadioButton selected={newConsultData.regraVTSemTxAesa} onClick={() => handleUsesNoTaxTicketsAesa(true)}>
                    Bilhete Comum {isCustomer ? '(mediante liminar da justiça)' : ''}
                    </FilterRadioButton>
                  </FilterRadioButtonsContainer>
                </FormGroup>

                <FormGroup>
                  <label htmlFor="contactName">Integrações</label>
                  <FilterRadioButtonsContainer>
                    <FilterRadioButton selected={!newConsultData.semIntegraVT} onClick={() => {
                      handleIsHighSalaryChange(false);
                      handleRJIntegration(true);
                    }}>
                    Realiza
                    </FilterRadioButton>
                    <FilterRadioButton selected={newConsultData.semIntegraVT} onClick={() => {
                      handleIsHighSalaryChange(true);
                      handleRJIntegration(false);
                    }}>
                    Não Realiza
                    </FilterRadioButton>
                  </FilterRadioButtonsContainer>
                </FormGroup>
              </InnerDataContainer>

              <InnerDataContainer>
                <h4>
                  <img src={user} alt="" />
                Critérios Colaborador
                </h4>
                <FormGroup error={getErrorMessageByFieldName('entryTime')}>
                  <label htmlFor="contactName">Horário Entrada</label>
                  <Input
                    type='time'
                    placeholder="00:00"
                    value={newConsultData.horaEntra}
                    onChange={handleEntryTimeChange}
                    autoComplete="new-password"
                    darkBorder
                    error={getErrorMessageByFieldName('entryTime')}
                  />
                </FormGroup>

                <FormGroup error={getErrorMessageByFieldName('exitTime')}>
                  <label htmlFor="contactName">Horário Saída</label>
                  <Input
                    type='time'
                    placeholder="00:00"
                    value={newConsultData.horaSai}
                    onChange={handleExitTimeChange}
                    autoComplete="new-password"
                    darkBorder
                    error={getErrorMessageByFieldName('exitTime')}
                  />
                </FormGroup>

                <FormGroup error={getErrorMessageByFieldName('workDays')}>
                  <label htmlFor="contactName">Turno (dias trabalhados/mês)</label>
                  <Input
                    placeholder="1 a 30"
                    value={newConsultData.diasVT ?? consultData.diasVT}
                    onChange={handleWorkDaysChange}
                    autoComplete="new-password"
                    darkBorder
                    error={getErrorMessageByFieldName('workDays')}
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="contactName">Salário</label>
                  <FilterRadioButtonsContainer>
                    <FilterRadioButton selected={newConsultData.salario <= 3200} onClick={() => {
                      handleIsHighSalaryChange(false);
                      handleRJIntegration(true);
                    }}>
                    Abaixo de 3205,20
                    </FilterRadioButton>
                    <FilterRadioButton selected={newConsultData.salario > 3200} onClick={() => {
                      handleIsHighSalaryChange(true);
                      handleRJIntegration(false);
                    }}>
                    Acima de 3205,20
                    </FilterRadioButton>
                  </FilterRadioButtonsContainer>
                </FormGroup>
              </InnerDataContainer>
            </SelectedViewContainer>
          )}

          {view === 'bias' && (
            <SelectedViewContainer>
              {(!isCustomer && !isMultiRoute) && (
                <InnerDataContainer mustOverflow>
                  <h4>
                    <img src={busStar} alt="" style={{ width: '24px' }} />
                    Bias
                    <FilterRadioButtonsContainer>
                      <FilterRadioButton selected={biasType === 'P'} onClick={() => changeBiasType('P')} disabled={!canChangeBiasType}>
                        Preferencial
                      </FilterRadioButton>
                      <FilterRadioButton selected={biasType === 'F'} onClick={() => changeBiasType('F')} disabled={!canChangeBiasType}>
                        Filtro
                      </FilterRadioButton>
                    </FilterRadioButtonsContainer>
                  </h4>
                  <FormGroup>
                    <label htmlFor="contactName">Bias Operadoras</label>
                    <Select
                      value={operatorBiasMultiValue}
                      options={fullOpsOptions}
                      onChange={(opt) => setCurrentOpsBias(opt.map((opt) => ({ codOperadora: opt.value, tipoBias: biasType })))}
                      placeholder="Selecione as operadoras BIAS"
                      isMulti
                      styles={CustomStyle}
                      components={animatedComponents}
                      closeMenuOnSelect={false}
                      classNamePrefix="react-select"
                      className="react-select-container"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="contactName">Bias Linhas</label>
                    <AsyncSelect
                      value={lineBiasMultiValue}
                      loadOptions={loadOptions} // Função chamada dinamicamente
                      onChange={(opt) =>
                        setCurrentLinesBias(
                          opt.map((opt) => ({
                            routeID: opt.value,
                            tipoBias: biasType,
                          }))
                        )
                      }
                      placeholder="Selecione as linhas BIAS"
                      isMulti
                      styles={CustomStyle}
                      components={animatedComponents}
                      closeMenuOnSelect={false}
                      classNamePrefix="react-select"
                      className="react-select-container"
                      noOptionsMessage={() => 'Digite ao menos 3 caracteres para carregar as opções filtradas'}
                    />
                  </FormGroup>

                  <h4 style={{ marginTop: '20px' }}>
                    <img src={busSlash} alt="" style={{ width: '24px' }} />
                    Exceções (Avoid)
                  </h4>
                  <FormGroup>
                    <label htmlFor="contactName">Exceções Operadoras</label>
                    <Select
                      value={operatorExceptionMultiValue}
                      options={fullOpsOptions}
                      onChange={(opt) => setCurrentOpsException(opt.map((opt) => ({ codOperadora: opt.value })))}
                      placeholder="Selecione as operadoras exceção"
                      isMulti
                      styles={CustomStyle}
                      components={animatedComponents}
                      closeMenuOnSelect={false}
                      classNamePrefix="react-select"
                      className="react-select-container"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="contactName">Exceções Linhas</label>
                    <AsyncSelect
                      value={lineExceptionMultiValue}
                      loadOptions={loadOptions}
                      onChange={(opt) => setCurrentLinesException(opt.map((opt) => ({ routeID: opt.value })))}
                      placeholder="Selecione as linhas de exceção"
                      isMulti
                      styles={CustomStyle}
                      components={animatedComponents}
                      closeMenuOnSelect={false}
                      classNamePrefix="react-select"
                      className="react-select-container"
                      noOptionsMessage={() => 'Digite ao menos 3 caracteres para carregar as opções filtradas'}
                    />
                  </FormGroup>
                </InnerDataContainer>
              )}
            </SelectedViewContainer>
          )}
        </>
      )}
    />
  );
}
